import { CSSProperties } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { VehicleStatus } from '../../../../shared/types/vehicle.types';

export const garageStyles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
    } satisfies SxProps<Theme>,
    header: {
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { sm: 'flex-start' },
        gap: 2,
    } satisfies SxProps<Theme>,
    searchPaper: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
    } satisfies SxProps<Theme>,
    searchField: {
        maxWidth: 300,
    } satisfies SxProps<Theme>,
    loading: {
        display: 'flex',
        justifyContent: 'center',
        mt: 4,
    } satisfies SxProps<Theme>,
    emptyPaper: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        py: 8,
        textAlign: 'center',
    } satisfies SxProps<Theme>,
    machineCard: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover': { boxShadow: 2 },
        transition: 'box-shadow 0.2s',
    } satisfies SxProps<Theme>,
    machineCardContent: {
        p: 3,
    } satisfies SxProps<Theme>,
    machineHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    } satisfies SxProps<Theme>,
    machineLink: {
        display: 'flex',
        gap: 2,
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': { opacity: 0.8 },
    } satisfies SxProps<Theme>,
    machineImage: {
        width: 64,
        height: 64,
        borderRadius: 1.5,
        overflow: 'hidden',
        flexShrink: 0,
        bgcolor: 'grey.100',
    } satisfies SxProps<Theme>,
    machineHeaderActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
    } satisfies SxProps<Theme>,
    warrantyRow: {
        mt: 3,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
    } satisfies SxProps<Theme>,
    outgoingTransferLink: {
        display: 'flex',
        flexDirection: 'column',
        mt: 2,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        textDecoration: 'none',
        color: 'inherit',
        gap: 1,
        '&:hover': { opacity: 0.7 },
    } satisfies SxProps<Theme>,
    transferTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    } satisfies SxProps<Theme>,
    transferLabelWithIcon: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
    } satisfies SxProps<Theme>,
    transferInfoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    } satisfies SxProps<Theme>,
    machineFooter: {
        bgcolor: 'grey.50',
        px: 3,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
    } satisfies SxProps<Theme>,
    machineFooterButton: {
        textTransform: 'none',
    } satisfies SxProps<Theme>,
    incomingTransferCard: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': { boxShadow: 2 },
        transition: 'box-shadow 0.2s',
    } satisfies SxProps<Theme>,
    incomingTransferHead: {
        p: 3,
    } satisfies SxProps<Theme>,
    incomingTransferHeadRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    } satisfies SxProps<Theme>,
    incomingTransferVehicle: {
        display: 'flex',
        gap: 2,
    } satisfies SxProps<Theme>,
    incomingTransferVehicleIcon: {
        width: 64,
        height: 64,
        borderRadius: 1.5,
        bgcolor: 'info.50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    } satisfies SxProps<Theme>,
    incomingTransferBody: {
        px: 3,
        pb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    } satisfies SxProps<Theme>,
    incomingTransferFooter: {
        bgcolor: 'grey.50',
        px: 3,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
    } satisfies SxProps<Theme>,
};

export const getGarageStatusNoteRowStyles = (hasWarranty: boolean): SxProps<Theme> => ({
    mt: hasWarranty ? 1 : 3,
    pt: hasWarranty ? 1 : 2,
    borderTop: hasWarranty ? 'none' : '1px solid',
    borderColor: 'divider',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
});

export const getGarageStatusNoteLabelStyles = (status: VehicleStatus): SxProps<Theme> => ({
    whiteSpace: 'nowrap',
    color: status === VehicleStatus.Rejected ? 'error.main' : 'info.main',
});

export const getTransferStatusColorStyles = (color: string): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    color: `${color}.main`,
});

export const outgoingTransferDirectionStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    color: 'warning.main',
};

export const incomingTransferDirectionStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    color: 'success.main',
};

export const garageMenuIconStyles: CSSProperties = {
    marginRight: 8,
};
